import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Archive, ArchiveRestore, ChevronLeft, Mail, MoreHorizontal, Pencil, Phone, Star } from "lucide-react";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Menu } from "~/components/ui/menu";
import { Page, PageHeader, Panel, Stat } from "~/components/ui/page";
import { ErrorState, Skeleton } from "~/components/ui/states";
import { History } from "~/features/audit/history";
import { CustomerActivity } from "~/features/customers/activity";
import { CustomerFormSheet } from "~/features/customers/customer-form";
import { customerQuery, KIND_TONE, type CustomerDetail } from "~/features/customers/types";
import { api, unwrap } from "~/lib/api";
import { errorMessageKey } from "~/lib/errors";
import { useFormat, useT } from "~/lib/i18n";
import { useCan } from "~/lib/session";
import { helpHint } from "~/features/help/hint";

export const Route = createFileRoute("/_app/customers/$id")({
  // Not awaited: the page renders its skeleton at once, then data or a retryable error.
  loader: ({ context, params }) => void context.queryClient.prefetchQuery(customerQuery(params.id)),
  component: CustomerPage,
});

function BackLink() {
  const t = useT();
  return (
    <Link to="/customers" className="mb-2 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg">
      <ChevronLeft className="size-4" aria-hidden />
      {t("customers.title")}
    </Link>
  );
}

function CustomerPage() {
  const { id } = Route.useParams();
  const query = useQuery(customerQuery(id));
  if (query.isPending) {
    return (
      <Page>
        <BackLink />
        <Skeleton className="h-8 w-72" />
        <Skeleton className="mt-6 h-64 w-full" />
      </Page>
    );
  }
  if (query.isError) {
    return (
      <Page>
        <BackLink />
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </Page>
    );
  }
  return <CustomerView customer={query.data} />;
}

function Section({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="border-b border-line px-5 py-5 last:border-b-0 md:px-6">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-[15px] font-medium">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function CustomerView({ customer }: { customer: CustomerDetail }) {
  const t = useT();
  const format = useFormat();
  const can = useCan();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const archived = Boolean(customer.archivedAt);

  const toggleArchive = useMutation({
    mutationFn: () =>
      unwrap(archived ? api.customers({ id: customer.id }).restore.post() : api.customers({ id: customer.id }).archive.post()),
    onSuccess: (saved) => {
      queryClient.setQueryData(["customers", customer.id], saved);
      void queryClient.invalidateQueries({ queryKey: ["customers", "list"] });
      void queryClient.invalidateQueries({ queryKey: ["audit", "customer", customer.id] });
      toast.success(archived ? t("common.actions.restore") : t("customers.archived"));
    },
    onError: (error) => toast.error(t(errorMessageKey(error))),
  });

  const addressBlock = (address: CustomerDetail["billingAddress"]) =>
    address ? (
      <address className="text-sm not-italic leading-relaxed">
        {format.address(address).map((line) => (
          <span key={line} className="block">
            {line}
          </span>
        ))}
      </address>
    ) : (
      <p className="text-sm text-fg-3">{t("common.states.none")}</p>
    );

  return (
    <Page>
      <PageHeader
        back={<BackLink />}
        title={customer.displayName}
        meta={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={KIND_TONE[customer.kind]}>{t(`customers.kinds.${customer.kind}`)}</Badge>
            {archived ? <Badge tone="warning">{t("customers.archived")}</Badge> : null}
            <span>{t("customers.since", { date: format.date(customer.createdAt, "long") })}</span>
          </span>
        }
        actions={
          can("customers:write") ? (
            <>
              <Button onClick={() => setEditing(true)}>
                <Pencil />
                {t("common.actions.edit")}
              </Button>
              <Menu
                trigger={
                  <Button variant="ghost" size="icon" aria-label={t("common.actions.more")}>
                    <MoreHorizontal />
                  </Button>
                }
                actions={[
                  {
                    label: archived ? t("common.actions.restore") : t("common.actions.archive"),
                    hint: archived ? undefined : helpHint(t, "customer.archive"),
                    icon: archived ? <ArchiveRestore /> : <Archive />,
                    onSelect: () => toggleArchive.mutate(),
                  },
                ]}
              />
            </>
          ) : null
        }
      />

      <div className="grid grid-cols-[minmax(0,1fr)] gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        <Panel>
          <Section title={t("customers.tabs.overview")}>
            <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 xl:grid-cols-3">
              <Stat
                label={t("common.fields.email")}
                value={
                  customer.email ? (
                    <a href={`mailto:${customer.email}`} className="inline-flex items-center gap-1.5 hover:underline">
                      <Mail className="size-3.5 text-fg-3" aria-hidden />
                      {customer.email}
                    </a>
                  ) : (
                    "—"
                  )
                }
              />
              <Stat
                label={t("common.fields.phone")}
                value={
                  customer.phone ? (
                    <a href={`tel:${customer.phone}`} className="inline-flex items-center gap-1.5 tabular hover:underline">
                      <Phone className="size-3.5 text-fg-3" aria-hidden />
                      {customer.phone}
                    </a>
                  ) : (
                    "—"
                  )
                }
              />
              <Stat
                label={t("customers.fields.paymentTermsDays")}
                value={<span className="tabular">{customer.paymentTermsDays}</span>}
              />
              {customer.kind !== "individual" ? (
                <>
                  <Stat label={t("customers.fields.siret")} value={<span className="tabular">{customer.siret ?? "—"}</span>} />
                  {customer.kind === "public" ? (
                    <Stat label={t("customers.fields.chorusServiceCode")} value={customer.chorusServiceCode ?? "—"} />
                  ) : (
                    <Stat label={t("customers.fields.vatNumber")} value={customer.vatNumber ?? "—"} />
                  )}
                </>
              ) : null}
              <Stat label={t("customers.fields.language")} value={t(`common.language.${customer.language}`)} />
            </dl>
          </Section>

          <Section title={t("common.fields.address")}>
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <p className="mb-1 text-xs text-fg-3">{t("customers.fields.billingAddress")}</p>
                {addressBlock(customer.billingAddress)}
              </div>
              <div>
                <p className="mb-1 text-xs text-fg-3">{t("customers.fields.shippingAddress")}</p>
                {customer.shippingAddress ? (
                  addressBlock(customer.shippingAddress)
                ) : (
                  <p className="text-sm text-fg-2">{t("customers.fields.sameAsBilling")}</p>
                )}
              </div>
            </div>
          </Section>

          {customer.kind !== "individual" ? (
            <Section title={t("customers.fields.contacts")}>
              {customer.contacts.length === 0 ? (
                <p className="text-sm text-fg-3">{t("customers.noContacts")}</p>
              ) : (
                <ul className="grid gap-3 sm:grid-cols-2">
                  {customer.contacts.map((contact) => (
                    <li key={contact.id} className="rounded-[10px] border border-line px-4 py-3">
                      <p className="flex items-center gap-1.5 text-sm font-medium">
                        {[contact.firstName, contact.lastName].filter(Boolean).join(" ")}
                        {contact.isPrimary ? (
                          <Star className="size-3.5 fill-gold text-gold" aria-label={t("customers.fields.primaryContact")} />
                        ) : null}
                      </p>
                      {contact.jobTitle ? <p className="text-xs text-fg-3">{contact.jobTitle}</p> : null}
                      <div className="mt-2 flex flex-col gap-1 text-sm text-fg-2">
                        {contact.email ? (
                          <a href={`mailto:${contact.email}`} className="truncate hover:underline">
                            {contact.email}
                          </a>
                        ) : null}
                        {contact.phone ? (
                          <a href={`tel:${contact.phone}`} className="tabular hover:underline">
                            {contact.phone}
                          </a>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          ) : null}

          {customer.notes ? (
            <Section title={t("common.fields.notes")}>
              <p className="text-sm whitespace-pre-line text-fg-2">{customer.notes}</p>
            </Section>
          ) : null}

          <Section title={t("customers.tabs.documents")}>
            <CustomerActivity customerId={customer.id} />
          </Section>
        </Panel>

        <aside>
          <Panel>
            <Section title={t("audit.history")}>
              <History entityType="customer" entityId={customer.id} />
            </Section>
          </Panel>
        </aside>
      </div>

      <CustomerFormSheet
        open={editing}
        onOpenChange={setEditing}
        customer={customer}
        onSaved={() => void queryClient.invalidateQueries({ queryKey: ["audit", "customer", customer.id] })}
      />
    </Page>
  );
}
